import { Link } from "react-router-dom";
import { ArrowRight, MapPin, CheckCircle2, Phone } from "lucide-react";
import { site } from "@/data/site";
import { services } from "@/data/services";
import { testimonials } from "@/data/testimonials";
import { projects } from "@/data/projects";
import ServiceCard from "@/components/ServiceCard";
import TestimonialCard from "@/components/TestimonialCard";
import ProjectCard from "@/components/ProjectCard";
import FacebookBadge from "@/components/FacebookBadge";
import ContactCta from "@/components/ContactCta";

export default function Home() {
    const featuredProjects = projects.slice(0, 3);

    return (
        <div data-testid="page-home">
            {/* HERO */}
            <section className="relative bg-white overflow-hidden border-b border-[var(--ns-border)]">
                <div className="container-ns pt-8 pb-16 sm:pt-12 sm:pb-20 lg:pt-16 lg:pb-24">
                    {/* Hero banner image */}
                    <div className="relative mx-auto max-w-5xl fade-up">
                        <div className="relative rounded-lg overflow-hidden border border-[var(--ns-border)] shadow-2xl shadow-[var(--ns-blue)]/10 bg-[var(--ns-blue-soft)]">
                            <img
                                src={site.heroImage}
                                alt="NS Tømrer og Ejendomsservice — lokal tømrer i Silkeborg"
                                className="w-full h-auto block"
                            />
                            <div className="absolute top-4 left-4 sm:top-6 sm:left-6">
                                <div className="inline-flex items-center gap-2 text-[var(--ns-blue)] bg-white/95 backdrop-blur px-3 py-1.5 sm:px-4 sm:py-2 rounded-full shadow-md">
                                    <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    <span className="overline !text-[10px] sm:!text-xs">
                                        {site.tagline}
                                    </span>
                                </div>
                            </div>
                            <div className="absolute top-4 right-4 sm:top-6 sm:right-6 bg-[var(--ns-blue)] text-white rounded-full px-3 py-2 sm:px-4 sm:py-3 shadow-xl flex items-center gap-2">
                                <span className="font-display font-black text-lg sm:text-2xl">
                                    100%
                                </span>
                                <span className="text-[9px] sm:text-[11px] uppercase tracking-widest font-bold leading-tight">
                                    Anbefalet
                                    <br />
                                    på Facebook
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Headline + CTAs under the hero image */}
                    <div className="max-w-4xl mx-auto text-center mt-10 sm:mt-14 fade-up fade-up-delay-2">
                        <h1 className="font-display font-black text-[var(--ns-text)] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tighter leading-[1.02] mb-6">
                            Ordentligt håndværk{" "}
                            <span className="text-[var(--ns-blue)]">
                                du kan stole på.
                            </span>
                        </h1>
                        <p className="text-lg sm:text-xl text-[var(--ns-muted)] leading-relaxed max-w-2xl mx-auto mb-8">
                            Jeg hedder Nicklas, og jeg hjælper dagligt kunder i
                            Silkeborg og omegn med både små og større
                            tømreropgaver — fra udskiftning af lister til
                            totalrenovering. Ledige tider lige nu.
                        </p>

                        <div className="flex flex-wrap gap-3 justify-center mb-8">
                            <Link
                                to="/kontakt"
                                data-testid="hero-cta-primary"
                                className="btn-primary"
                            >
                                Få et uforpligtende tilbud
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                            <a
                                href={site.phoneHref}
                                data-testid="hero-cta-call"
                                className="btn-secondary"
                            >
                                <Phone className="w-4 h-4" />
                                Ring {site.phone}
                            </a>
                        </div>

                        <div className="flex flex-wrap items-center gap-4 justify-center">
                            <FacebookBadge />
                            <div className="flex items-center gap-2 text-sm text-[var(--ns-muted)]">
                                <CheckCircle2 className="w-4 h-4 text-[var(--ns-blue)]" />
                                Lokal i Silkeborg og omegn
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* MEET NICKLAS */}
            <section
                data-testid="meet-nicklas"
                className="py-20 sm:py-28 bg-white border-b border-[var(--ns-border)]"
            >
                <div className="container-ns">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <div className="lg:col-span-5 relative">
                            <div className="relative aspect-[4/5] overflow-hidden rounded-lg shadow-2xl shadow-[var(--ns-blue)]/20 bg-[var(--ns-blue-soft)]">
                                <img
                                    src={site.portrait}
                                    alt="Nicklas Sørensen — ejer af NS Tømrer og Ejendomsservice"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ns-blue)]/25 via-transparent to-transparent pointer-events-none" />
                            </div>
                            {/* Blue block accent with logo */}
                            <div className="hidden sm:flex absolute -top-6 -left-6 w-32 h-32 bg-[var(--ns-blue)] rounded-md -z-0 p-3 items-center justify-center shadow-xl">
                                <div className="w-full h-full bg-white rounded-sm flex items-center justify-center p-2">
                                    <img
                                        src={site.logo}
                                        alt="NS Tømrer og Ejendomsservice logo"
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                            </div>
                            {/* Name card overlapping */}
                            <div className="absolute -bottom-6 right-4 sm:right-8 bg-white border border-[var(--ns-border)] rounded-md p-5 shadow-xl max-w-[260px]">
                                <div className="overline mb-1">Ejer</div>
                                <div className="font-display font-extrabold text-lg leading-tight mb-1">
                                    Nicklas Sørensen
                                </div>
                                <div className="text-sm text-[var(--ns-muted)]">
                                    Tømrer & ejendomsservice · Silkeborg
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-7 lg:pl-8">
                            <div className="overline mb-4">Mød tømreren</div>
                            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.03] mb-6 text-[var(--ns-text)]">
                                Jeg hedder{" "}
                                <span className="text-[var(--ns-blue)]">
                                    Nicklas
                                </span>{" "}
                                — og jeg driver NS Tømrer.
                            </h2>
                            <blockquote className="border-l-4 border-[var(--ns-blue)] pl-5 py-1 mb-6">
                                <p className="font-display font-extrabold text-xl sm:text-2xl tracking-tight text-[var(--ns-text)] leading-snug">
                                    &quot;Jeg kommer gerne ud, tager en snak og
                                    giver et helt uforpligtende tilbud.&quot;
                                </p>
                            </blockquote>
                            <p className="text-lg text-[var(--ns-muted)] leading-relaxed mb-6">
                                For mig handler det ikke bare om at blive
                                færdig hurtigt, men om at levere ordentligt
                                håndværk, god dialog og løsninger, du bliver
                                glad for i længden.
                            </p>
                            <p className="text-lg text-[var(--ns-muted)] leading-relaxed mb-8">
                                Som kunde er det mig, du møder på pladsen, i
                                telefonen og i mailen. Ingen mellemled — kun
                                ærlig snak om pris, plan og forventninger. Det
                                er der mine kunders 100 % anbefalings­stempel
                                kommer fra.
                            </p>
                            <Link
                                to="/om"
                                data-testid="meet-nicklas-cta"
                                className="btn-primary"
                            >
                                Læs hele historien
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="py-20 sm:py-28 bg-[var(--ns-bg)]">
                <div className="container-ns">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
                        <div className="max-w-2xl">
                            <div className="overline mb-4">Det jeg laver</div>
                            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05] text-[var(--ns-text)]">
                                Fire kerne­ydelser —<br />
                                små som store opgaver.
                            </h2>
                        </div>
                        <Link
                            to="/ydelser"
                            data-testid="services-see-all"
                            className="link-underline font-display font-bold text-sm text-[var(--ns-blue)] inline-flex items-center gap-2 self-start lg:self-auto"
                        >
                            Se alle ydelser
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {services.map((s, i) => (
                            <ServiceCard key={s.id} service={s} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURED PROJECTS */}
            <section className="py-20 sm:py-28 bg-white border-y border-[var(--ns-border)]">
                <div className="container-ns">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
                        <div className="max-w-2xl">
                            <div className="overline mb-4">Udvalgte projekter</div>
                            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
                                Et par af de seneste jobs.
                            </h2>
                        </div>
                        <Link
                            to="/projekter"
                            data-testid="projects-see-all"
                            className="link-underline font-display font-bold text-sm text-[var(--ns-blue)] inline-flex items-center gap-2"
                        >
                            Se hele galleriet
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {featuredProjects.map((p, i) => (
                            <ProjectCard key={p.id} project={p} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="py-20 sm:py-28 bg-[var(--ns-bg)]">
                <div className="container-ns">
                    <div className="max-w-2xl mb-14">
                        <div className="overline mb-4">Kundernes ord</div>
                        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
                            100 % anbefalet på Facebook.
                        </h2>
                        <p className="text-lg text-[var(--ns-muted)] leading-relaxed mt-5">
                            Alle kunder anbefaler NS Tømrer og Ejendomsservice
                            — her er tre af de seneste Facebook-anmeldelser.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {testimonials.map((t, i) => (
                            <TestimonialCard key={i} t={t} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            <ContactCta />
        </div>
    );
}
